"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.shouldUseMatchForFulfill = exports.generateRandomSalt = exports.adjustTipsForPartialFills = exports.mapOrderAmountsFromUnitsToFill = exports.mapOrderAmountsFromFilledStatus = exports.totalItemsAmount = exports.areAllCurrenciesSame = exports.mapInputItemToOfferItem = exports.deductFees = exports.feeToConsiderationItem = void 0;
const ethers_1 = require("ethers");
const constants_1 = require("../constants");
const item_1 = require("./item");
const merkletree_1 = require("./merkletree");
const multiplyBasisPoints = (amount, basisPoints) => (BigInt(amount) * BigInt(basisPoints)) / constants_1.ONE_HUNDRED_PERCENT_BP;
const feeToConsiderationItem = ({ fee, token, baseAmount, baseEndAmount = baseAmount, }) => {
    return {
        itemType: token === ethers_1.ethers.ZeroAddress ? constants_1.ItemType.NATIVE : constants_1.ItemType.ERC20,
        token,
        identifierOrCriteria: "0",
        startAmount: multiplyBasisPoints(baseAmount, fee.basisPoints).toString(),
        endAmount: multiplyBasisPoints(baseEndAmount, fee.basisPoints).toString(),
        recipient: fee.recipient,
    };
};
exports.feeToConsiderationItem = feeToConsiderationItem;
const deductFees = (items, fees) => {
    if (!fees) {
        return items;
    }
    const totalBasisPoints = fees.reduce((accBasisPoints, fee) => accBasisPoints + fee.basisPoints, 0);
    return items.map((item) => ({
        ...item,
        startAmount: (0, item_1.isCurrencyItem)(item)
            ? (BigInt(item.startAmount) -
                multiplyBasisPoints(item.startAmount, totalBasisPoints)).toString()
            : item.startAmount,
        endAmount: (0, item_1.isCurrencyItem)(item)
            ? (BigInt(item.endAmount) -
                multiplyBasisPoints(item.endAmount, totalBasisPoints)).toString()
            : item.endAmount,
    }));
};
exports.deductFees = deductFees;
const mapInputItemToOfferItem = (item) => {
    if ("itemType" in item) {
        // Convert this to a criteria based item
        if ("identifiers" in item || "criteria" in item) {
            const root = "criteria" in item
                ? item.criteria
                : new merkletree_1.MerkleTree(item.identifiers).getRoot();
            return {
                itemType: item.itemType === constants_1.ItemType.ERC721
                    ? constants_1.ItemType.ERC721_WITH_CRITERIA
                    : constants_1.ItemType.ERC1155_WITH_CRITERIA,
                token: item.token,
                identifierOrCriteria: root,
                startAmount: item.amount ?? "1",
                endAmount: item.endAmount ?? item.amount ?? "1",
            };
        }
        if ("amount" in item || "endAmount" in item) {
            return {
                itemType: item.itemType,
                token: item.token,
                // prevent undefined for fungible items
                identifierOrCriteria: item.identifier ?? "0",
                // @ts-ignore
                startAmount: item.amount,
                // @ts-ignore
                endAmount: item.endAmount ?? item.amount ?? "1",
            };
        }
        return {
            itemType: item.itemType,
            token: item.token,
            identifierOrCriteria: item.identifier,
            startAmount: "1",
            endAmount: "1",
        };
    }
    // Item is a currency
    return {
        itemType: item.token && item.token !== ethers_1.ethers.ZeroAddress
            ? constants_1.ItemType.ERC20
            : constants_1.ItemType.NATIVE,
        token: item.token ?? ethers_1.ethers.ZeroAddress,
        identifierOrCriteria: "0",
        startAmount: item.amount,
        endAmount: item.endAmount ?? item.amount,
    };
};
exports.mapInputItemToOfferItem = mapInputItemToOfferItem;
const areAllCurrenciesSame = ({ offer, consideration, }) => {
    const allItems = [...offer, ...consideration];
    const currencies = allItems.filter(item_1.isCurrencyItem);
    return currencies.every(({ itemType, token }) => itemType === currencies[0].itemType &&
        token.toLowerCase() === currencies[0].token.toLowerCase());
};
exports.areAllCurrenciesSame = areAllCurrenciesSame;
const totalItemsAmount = (items) => {
    const initialValues = {
        startAmount: 0n,
        endAmount: 0n,
    };
    return items
        .map(({ startAmount, endAmount }) => ({
        startAmount,
        endAmount,
    }))
        .reduce(({ startAmount: totalStartAmount, endAmount: totalEndAmount }, { startAmount, endAmount }) => ({
        startAmount: totalStartAmount + BigInt(startAmount),
        endAmount: totalEndAmount + BigInt(endAmount),
    }), {
        startAmount: 0n,
        endAmount: 0n,
    });
};
exports.totalItemsAmount = totalItemsAmount;
/**
 * Maps order offer and consideration item amounts based on the order's filled status
 * After applying the fraction, we can view this order as the "canonical" order for which we
 * check approvals and balances
 */
const mapOrderAmountsFromFilledStatus = (order, { totalFilled, totalSize }) => {
    if (totalFilled === 0n || totalSize === 0n) {
        return order;
    }
    // i.e if totalFilled is 3 and totalSize is 4, there are 1 / 4 order amounts left to fill.
    const basisPoints = totalSize - (totalFilled * constants_1.ONE_HUNDRED_PERCENT_BP) / totalSize;
    return {
        parameters: {
            ...order.parameters,
            offer: order.parameters.offer.map((item) => ({
                ...item,
                startAmount: multiplyBasisPoints(item.startAmount, basisPoints).toString(),
                endAmount: multiplyBasisPoints(item.endAmount, basisPoints).toString(),
            })),
            consideration: order.parameters.consideration.map((item) => ({
                ...item,
                startAmount: multiplyBasisPoints(item.startAmount, basisPoints).toString(),
                endAmount: multiplyBasisPoints(item.endAmount, basisPoints).toString(),
            })),
        },
        signature: order.signature,
    };
};
exports.mapOrderAmountsFromFilledStatus = mapOrderAmountsFromFilledStatus;
const multiplyDivision = (amount, numerator, denominator) => (BigInt(amount) * BigInt(numerator)) / BigInt(denominator);
/**
 * Maps order offer and consideration item amounts based on the units needed to fulfill
 * After applying the fraction, we can view this order as the "canonical" order for which we
 * check approvals and balances
 * Returns the numerator and denominator as well, converting this to an AdvancedOrder
 */
const mapOrderAmountsFromUnitsToFill = (order, { unitsToFill, totalSize }) => {
    const unitsToFillBn = BigInt(unitsToFill);
    if (unitsToFillBn <= 0n) {
        throw new Error("Units to fill must be greater than 1");
    }
    const maxUnits = (0, item_1.getMaximumSizeForOrder)(order);
    if (totalSize === 0n) {
        totalSize = maxUnits;
    }
    return {
        parameters: {
            ...order.parameters,
            offer: order.parameters.offer.map((item) => ({
                ...item,
                startAmount: multiplyDivision(item.startAmount, unitsToFillBn, totalSize).toString(),
                endAmount: multiplyDivision(item.endAmount, unitsToFillBn, totalSize).toString(),
            })),
            consideration: order.parameters.consideration.map((item) => ({
                ...item,
                startAmount: multiplyDivision(item.startAmount, unitsToFillBn, totalSize).toString(),
                endAmount: multiplyDivision(item.endAmount, unitsToFillBn, totalSize).toString(),
            })),
        },
        signature: order.signature,
    };
};
exports.mapOrderAmountsFromUnitsToFill = mapOrderAmountsFromUnitsToFill;
function adjustTipsForPartialFills(tips, unitsToFill, totalSize) {
    const unitsToFillBn = BigInt(unitsToFill);
    if (unitsToFillBn <= 0n) {
        throw new Error("Units to fill must be greater than 0");
    }
    return tips.map((tip) => ({
        ...tip,
        startAmount: multiplyDivision(tip.startAmount, unitsToFillBn, totalSize).toString(),
        endAmount: multiplyDivision(tip.endAmount, unitsToFillBn, totalSize).toString(),
    }));
}
exports.adjustTipsForPartialFills = adjustTipsForPartialFills;
const generateRandomSalt = (domain) => {
    if (domain) {
        return (0, ethers_1.toBeHex)((0, ethers_1.concat)([
            (0, ethers_1.keccak256)((0, ethers_1.toUtf8Bytes)(domain)).slice(0, 10),
            Uint8Array.from(Array(20).fill(0)),
            (0, ethers_1.randomBytes)(8),
        ]));
    }
    return `0x${Buffer.from((0, ethers_1.randomBytes)(8)).toString("hex").padStart(64, "0")}`;
};
exports.generateRandomSalt = generateRandomSalt;
const shouldUseMatchForFulfill = () => true;
exports.shouldUseMatchForFulfill = shouldUseMatchForFulfill;
//# sourceMappingURL=order.js.map